import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Input, DatePicker, Select, Space, Tag, Button, Row, Col, Statistic, Typography, message, Tooltip, Segmented, Alert } from 'antd';
import { ReloadOutlined, DownloadOutlined, ClearOutlined, SyncOutlined, MessageOutlined } from '@ant-design/icons';
import ModalComunicacao from '../components/ModalComunicacao';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Relatorio de boletos emitidos. Os dados vem todos de uma vez (GET /boletos/relatorio)
// e filtro, busca e ordenacao sao feitos aqui na tela: o volume e pequeno e assim
// qualquer combinacao responde na hora.

const STATUS = {
  emitido: { rotulo: 'Em aberto', cor: 'blue' },
  vencido: { rotulo: 'Vencido', cor: 'red' },
  pago: { rotulo: 'Pago', cor: 'green' },
  cancelado: { rotulo: 'Cancelado', cor: 'default' },
  expirado: { rotulo: 'Expirado', cor: 'orange' },
};
const so = (v) => (v === null || v === undefined ? '' : String(v));
const digitos = (v) => so(v).replace(/\D/g, '');
const semAcento = (v) => so(v).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const moeda = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBR = (v) => (v ? dayjs(String(v).slice(0, 10)).format('DD/MM/YYYY') : '-');
const fmtCNPJ = (v) => {
  const d = digitos(v);
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return so(v) || '-';
};
const mesAtual = () => [dayjs().startOf('month'), dayjs().endOf('month')];

export default function Relatorios() {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [campoData, setCampoData] = useState('emissao');
  const [periodo, setPeriodo] = useState(mesAtual());
  const [status, setStatus] = useState([]);
  const [tipo, setTipo] = useState('todos');
  const [sincronizando, setSincronizando] = useState({});
  const [lote, setLote] = useState(null);
  const [comunicar, setComunicar] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const r = await api.get('/boletos/relatorio');
      setDados(r.data || []);
    } catch (e) {
      message.error(e.response?.status === 403 ? 'Seu perfil não tem acesso a boletos.' : 'Não foi possível carregar os boletos');
    } finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, []);

  const filtrados = useMemo(() => {
    const t = semAcento(busca.trim());
    const tDig = digitos(busca);
    return dados.filter((b) => {
      if (periodo && periodo[0] && periodo[1]) {
        const ref = campoData === 'vencimento' ? b.vencimento : b.criado_em;
        if (!ref) return false;
        const d = dayjs(String(ref).slice(0, 10));
        if (d.isBefore(periodo[0].startOf('day')) || d.isAfter(periodo[1].endOf('day'))) return false;
      }
      if (status.length && status.indexOf(b.status_exibido) < 0) return false;
      if (tipo !== 'todos' && b.tipo !== tipo) return false;
      if (t) {
        const texto = semAcento([b.sacado, b.razao_social, b.nome_cliente, b.proposta_numero, b.nosso_numero].join(' '));
        const achouDig = tDig.length >= 3 && digitos(b.cnpj).indexOf(tDig) >= 0;
        if (texto.indexOf(t) < 0 && !achouDig) return false;
      }
      return true;
    });
  }, [dados, busca, campoData, periodo, status, tipo]);

  const totais = useMemo(() => {
    const soma = (f) => filtrados.filter(f).reduce((a, b) => a + (Number(b.valor) || 0), 0);
    return {
      total: soma((b) => b.status_exibido !== 'cancelado'),
      pago: soma((b) => b.status_exibido === 'pago'),
      aberto: soma((b) => b.status_exibido === 'emitido'),
      vencido: soma((b) => b.status_exibido === 'vencido'),
    };
  }, [filtrados]);

  // pendencias de todo o periodo (ignora os filtros da tela)
  const pendentes = useMemo(() => {
    const r = { vencido: { qtd: 0, valor: 0 }, expirado: { qtd: 0, valor: 0 } };
    dados.forEach((b) => { const k = r[b.status_exibido]; if (k) { k.qtd++; k.valor += Number(b.valor) || 0; } });
    return r;
  }, [dados]);
  const verPendentes = () => { setBusca(''); setPeriodo(null); setTipo('todos'); setStatus(['vencido', 'expirado']); };

  const limpar = () => { setBusca(''); setCampoData('emissao'); setPeriodo(mesAtual()); setStatus([]); setTipo('todos'); };

  const exportarCSV = () => {
    const cab = ['Emissão', 'Vencimento', 'Cliente / Sacado', 'CNPJ/CPF', 'Proposta', 'Tipo', 'Valor', 'Status', 'Nosso número'];
    const linhas = filtrados.map((b) => [dataBR(b.criado_em), dataBR(b.vencimento), b.sacado || b.razao_social || b.nome_cliente || '',
      fmtCNPJ(b.cnpj), b.proposta_numero || '', b.tipo === 'produto' ? 'Produto' : 'Serviço',
      (Number(b.valor) || 0).toFixed(2).replace('.', ','), (STATUS[b.status_exibido] || {}).rotulo || b.status_exibido, b.nosso_numero || '']);
    const csv = '\ufeff' + [cab].concat(linhas).map((l) => l.map((c) => '"' + so(c).replace(/"/g, '""') + '"').join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'boletos_' + dayjs().format('YYYY-MM-DD') + '.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // consulta o boleto no Inter e grava o status (mesma rota do botao da proposta)
  const sincronizar = async (b, silencioso) => {
    setSincronizando((s) => ({ ...s, [b.id]: true }));
    try {
      await api.get('/boletos/' + b.id + '/status', { timeout: 60000 });
      if (!silencioso) { message.success('Status atualizado com o Inter'); await carregar(); }
      return true;
    } catch (e) {
      if (!silencioso) message.error('Inter não respondeu: ' + (e.response?.data?.erro || e.message));
      return false;
    } finally { setSincronizando((s) => { const n = { ...s }; delete n[b.id]; return n; }); }
  };
  // todos os boletos em aberto/vencidos que estao na tela, um por vez (o Inter limita chamadas)
  const sincronizarTodos = async () => {
    const alvo = filtrados.filter((b) => b.status_exibido === 'emitido' || b.status_exibido === 'vencido');
    if (!alvo.length) { message.info('Nenhum boleto em aberto ou vencido nesta lista.'); return; }
    let ok = 0, falha = 0;
    for (let i = 0; i < alvo.length; i++) {
      setLote(i + 1 + '/' + alvo.length);
      if (await sincronizar(alvo[i], true)) ok++; else falha++;
      await new Promise((r) => setTimeout(r, 400));
    }
    setLote(null);
    await carregar();
    if (falha) message.warning(ok + ' atualizado(s), ' + falha + ' sem resposta do Inter.');
    else message.success(ok + ' boleto(s) conferido(s) com o Inter.');
  };

  const abrirProposta = (b) => {
    if (b.tipo === 'produto' && b.proposta_produto_id) navigate('/propostas-produtos/' + b.proposta_produto_id);
    else if (b.proposta_id) navigate('/propostas/' + b.proposta_id);
  };

  const cmpTexto = (c) => (a, b) => semAcento(a[c]).localeCompare(semAcento(b[c]));
  const cmpData = (c) => (a, b) => so(a[c]).localeCompare(so(b[c]));
  const ORDEM_STATUS = ['vencido', 'emitido', 'pago', 'expirado', 'cancelado'];

  const colunas = [
    { title: 'Ações', width: 80, fixed: 'left', render: (_, b) => (
      <Space size={2}>
        <Tooltip title="Atualizar status com o Inter">
          <Button size="small" icon={<SyncOutlined spin={!!sincronizando[b.id]} />} disabled={!!sincronizando[b.id] || !!lote}
            style={{ color: '#1677ff', borderColor: '#1677ff' }} onClick={() => sincronizar(b)} />
        </Tooltip>
        <Tooltip title="Comunicação (enviar e-mail)">
          <Button size="small" icon={<MessageOutlined />} style={{ color: '#08979c', borderColor: '#08979c' }}
            disabled={!(b.proposta_id || b.proposta_produto_id)}
            onClick={() => setComunicar(b)} />
        </Tooltip>
      </Space>) },
    { title: 'Emissão', dataIndex: 'criado_em', width: 105, render: dataBR, sorter: cmpData('criado_em'), defaultSortOrder: 'descend' },
    { title: 'Vencimento', dataIndex: 'vencimento', width: 110, render: dataBR, sorter: cmpData('vencimento') },
    { title: 'Cliente / Sacado', dataIndex: 'sacado', sorter: cmpTexto('sacado'), render: (v, b) => (
      <div>
        <Text strong>{v || b.razao_social || b.nome_cliente || '-'}</Text>
        {b.razao_social && v && b.razao_social !== v ? <div style={{ fontSize: 12, color: '#888' }}>{b.razao_social}</div> : null}
      </div>) },
    { title: 'CNPJ/CPF', dataIndex: 'cnpj', width: 165, render: fmtCNPJ, sorter: cmpTexto('cnpj') },
    { title: 'Proposta', dataIndex: 'proposta_numero', width: 125, sorter: cmpTexto('proposta_numero'), render: (v, b) => (
      v ? <Button type="link" style={{ padding: 0 }} onClick={() => abrirProposta(b)}>{v}</Button> : '-') },
    { title: 'Tipo', dataIndex: 'tipo', width: 90, sorter: cmpTexto('tipo'),
      render: (v) => (v === 'produto' ? <Tag color="purple">Produto</Tag> : <Tag color="geekblue">Serviço</Tag>) },
    { title: 'Valor', dataIndex: 'valor', width: 125, align: 'right', render: moeda,
      sorter: (a, b) => (Number(a.valor) || 0) - (Number(b.valor) || 0) },
    { title: 'Status', dataIndex: 'status_exibido', width: 115,
      sorter: (a, b) => ORDEM_STATUS.indexOf(a.status_exibido) - ORDEM_STATUS.indexOf(b.status_exibido),
      render: (v, b) => {
        const s = STATUS[v] || { rotulo: v, cor: 'default' };
        const tag = <Tag color={s.cor}>{s.rotulo}</Tag>;
        return v === 'pago' && b.atualizado_em
          ? <Tooltip title={'Baixa registrada em ' + dataBR(b.atualizado_em)}>{tag}</Tooltip> : tag;
      } },
    { title: 'Nosso número', dataIndex: 'nosso_numero', width: 130, sorter: cmpTexto('nosso_numero'), render: (v) => v || '-' },
  ];

  return (
    <div>
      <Card title="Relatório de boletos" extra={
        <Space>
          <Button icon={<DownloadOutlined />} onClick={exportarCSV} disabled={!filtrados.length}>Exportar planilha</Button>
          <Tooltip title="Consulta no Inter todos os boletos em aberto ou vencidos desta lista">
            <Button icon={<SyncOutlined spin={!!lote} />} onClick={sincronizarTodos} disabled={!!lote || carregando}>{lote ? 'Conferindo ' + lote : 'Atualizar status (Inter)'}</Button>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={carregar} loading={carregando}>Recarregar lista</Button>
        </Space>}>
        {(pendentes.vencido.qtd > 0 || pendentes.expirado.qtd > 0) ? (
          <Alert type="error" showIcon style={{ marginBottom: 16 }}
            message={<span><strong>Em atraso (todo o período): {moeda(pendentes.vencido.valor + pendentes.expirado.valor)}</strong></span>}
            description={<span>
              Vencidos: <strong>{pendentes.vencido.qtd}</strong> boleto(s) — {moeda(pendentes.vencido.valor)}
              {'   ·   '}Expirados: <strong>{pendentes.expirado.qtd}</strong> boleto(s) — {moeda(pendentes.expirado.valor)}
            </span>}
            action={<Button size="small" danger onClick={verPendentes}>Ver esses boletos</Button>} />
        ) : (!carregando && dados.length > 0 ? (
          <Alert type="success" showIcon style={{ marginBottom: 16 }} message="Nenhum boleto vencido ou expirado em todo o período." />
        ) : null)}
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Input.Search allowClear placeholder="Buscar por cliente, CNPJ, proposta ou nosso número"
              value={busca} onChange={(e) => setBusca(e.target.value)} />
          </Col>
          <Col xs={24} md={9}>
            <Space.Compact style={{ width: '100%' }}>
              <Select value={campoData} onChange={setCampoData} style={{ width: 140 }}
                options={[{ value: 'emissao', label: 'Emissão' }, { value: 'vencimento', label: 'Vencimento' }]} />
              <RangePicker format="DD/MM/YYYY" value={periodo} onChange={(v) => setPeriodo(v)} allowClear
                style={{ width: '100%' }} placeholder={['Início', 'Fim']}
                presets={[
                  { label: 'Este mês', value: mesAtual() },
                  { label: 'Mês passado', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                  { label: 'Próximos 30 dias', value: [dayjs(), dayjs().add(30, 'day')] },
                  { label: 'Este ano', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
                ]} />
            </Space.Compact>
          </Col>
          <Col xs={24} md={4}>
            <Select mode="multiple" allowClear placeholder="Status" value={status} onChange={setStatus} style={{ width: '100%' }}
              options={Object.entries(STATUS).map(([k, s]) => ({ value: k, label: s.rotulo }))} />
          </Col>
          <Col xs={24} md={3}>
            <Button icon={<ClearOutlined />} onClick={limpar} block>Limpar</Button>
          </Col>
          <Col xs={24}>
            <Space wrap>
              <Segmented value={tipo} onChange={setTipo}
                options={[{ value: 'todos', label: 'Todos' }, { value: 'servico', label: 'Serviços' }, { value: 'produto', label: 'Produtos' }]} />
              <Text type="secondary">
                {periodo && periodo[0] ? (campoData === 'vencimento' ? 'Vencimento' : 'Emissão') + ' de ' + periodo[0].format('DD/MM/YYYY') + ' a ' + periodo[1].format('DD/MM/YYYY') : 'Todo o período'}
                {' · '}{filtrados.length} boleto(s)
              </Text>
            </Space>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Total (sem cancelados)" value={moeda(totais.total)} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Pago" value={moeda(totais.pago)} valueStyle={{ color: '#389e0d' }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Em aberto" value={moeda(totais.aberto)} valueStyle={{ color: '#1677ff' }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Vencido" value={moeda(totais.vencido)} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        </Row>

        <Table rowKey="id" size="small" columns={colunas} dataSource={filtrados} loading={carregando}
          scroll={{ x: 1180 }} pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 500] }}
          locale={{ emptyText: 'Nenhum boleto com esses filtros.' }} />
      </Card>
      <ModalComunicacao open={!!comunicar}
        propostaId={comunicar ? (comunicar.tipo === 'produto' ? comunicar.proposta_produto_id : comunicar.proposta_id) : null}
        tipoProposta={comunicar ? comunicar.tipo : undefined}
        boletoId={comunicar ? comunicar.id : undefined}
        onClose={() => setComunicar(null)} />
    </div>
  );
}
